import type { HttpLayer } from '../http.js';

export const CATALOG_UNSUBSCRIBE_DEBOUNCE_MS = 1000;

export const CATALOG_SCOPES = [
  'persona',
  'skills',
  'secrets',
  'mcp',
  'agentProcesses',
  'workspaces',
  'sessions',
  'integrations',
  'orgRoster',
  'context',
  'billing',
  'work',
  'orgchart',
] as const;

export type CatalogScopeType = (typeof CATALOG_SCOPES)[number];

export interface CatalogChanged {
  scope_type: string;
  scope_id: string;
  id: string;
  /** Client-only subscribe refresh; absent on `catalog.changed` frames (app-shell ADR-026). */
  cause?: 'subscribe';
}

export type CatalogChangedHandler = (change: CatalogChanged) => void;

export interface CatalogRegistrar {
  subscribe(deviceId: string, scopeType: string, scopeId: string): Promise<unknown>;
  unsubscribe(deviceId: string, scopeType: string, scopeId: string): Promise<unknown>;
}

export class CatalogsResource {
  constructor(private http: HttpLayer) {}

  async subscribe(deviceId: string, scopeType: string, scopeId: string): Promise<{ status: string }> {
    return this.http.post('/v1/catalogs/subscribe', {
      device_id: deviceId,
      scope_type: scopeType,
      scope_id: scopeId,
    });
  }

  async unsubscribe(deviceId: string, scopeType: string, scopeId: string): Promise<{ status: string }> {
    return this.http.post('/v1/catalogs/unsubscribe', {
      device_id: deviceId,
      scope_type: scopeType,
      scope_id: scopeId,
    });
  }
}

export const CATALOG_RESUBSCRIBE_STAGGER_MS = 100;

function catalogKey(scopeType: string, scopeId: string): string {
  return `${scopeType}\0${scopeId}`;
}

interface CatalogScopeEntry {
  readonly scopeType: string;
  readonly scopeId: string;
  readonly handlers: Set<CatalogChangedHandler>;
  subscribed: boolean;
  chain: Promise<void>;
  lastOut: ReturnType<typeof setTimeout> | null;
}

// Last-one-out holders, per-scope serialized control plane (app-shell ADR-026).
export class CatalogHolderRegistry {
  private readonly entries = new Map<string, CatalogScopeEntry>();
  private readonly resubscribeTimers = new Set<ReturnType<typeof setTimeout>>();
  private opened = false;

  constructor(
    private readonly deviceId: () => string,
    private readonly registrar: CatalogRegistrar,
    private readonly waitReady: () => Promise<void>,
    private readonly onError: (err: unknown) => void
  ) {}

  dispatch(change: CatalogChanged): void {
    const entry = this.entries.get(catalogKey(change.scope_type, change.scope_id));
    if (!entry) return;
    for (const handler of entry.handlers) handler(change);
  }

  register(
    scopeType: string,
    scopeId: string,
    handler: CatalogChangedHandler
  ): { registered: Promise<void>; unsubscribe: () => void } {
    const entry = this.entryFor(scopeType, scopeId);
    if (entry.lastOut !== null) {
      clearTimeout(entry.lastOut);
      entry.lastOut = null;
    }
    entry.handlers.add(handler);
    const registered = this.reconcile(entry);

    const unsubscribe = (): void => {
      entry.handlers.delete(handler);
      if (entry.handlers.size > 0 || entry.lastOut !== null) return;
      entry.lastOut = setTimeout(() => {
        entry.lastOut = null;
        this.reconcile(entry).catch(this.onError);
      }, CATALOG_UNSUBSCRIBE_DEBOUNCE_MS);
    };

    return { registered, unsubscribe };
  }

  resubscribeAll(): void {
    if (!this.opened) {
      this.opened = true;
      return;
    }
    let slot = 0;
    for (const entry of this.entries.values()) {
      entry.subscribed = false;
      if (entry.handlers.size === 0) continue;
      const delay = slot * CATALOG_RESUBSCRIBE_STAGGER_MS + Math.random() * (CATALOG_RESUBSCRIBE_STAGGER_MS / 2);
      slot += 1;
      const timer = setTimeout(() => {
        this.resubscribeTimers.delete(timer);
        this.reconcile(entry)
          .then(() => {
            if (!entry.subscribed) return;
            const change: CatalogChanged = {
              scope_type: entry.scopeType,
              scope_id: entry.scopeId,
              id: entry.scopeId,
              cause: 'subscribe',
            };
            for (const handler of entry.handlers) handler(change);
          })
          .catch(this.onError);
      }, delay);
      this.resubscribeTimers.add(timer);
    }
  }

  releaseAll(): Promise<void> {
    const released: Promise<void>[] = [];
    for (const entry of this.entries.values()) {
      if (entry.lastOut !== null) {
        clearTimeout(entry.lastOut);
        entry.lastOut = null;
      }
      entry.handlers.clear();
      released.push(this.reconcile(entry));
    }
    return Promise.all(released).then(() => undefined);
  }

  close(): void {
    for (const entry of this.entries.values()) {
      if (entry.lastOut !== null) clearTimeout(entry.lastOut);
      entry.lastOut = null;
      entry.handlers.clear();
    }
    for (const timer of this.resubscribeTimers) clearTimeout(timer);
    this.resubscribeTimers.clear();
    this.entries.clear();
  }

  private entryFor(scopeType: string, scopeId: string): CatalogScopeEntry {
    const key = catalogKey(scopeType, scopeId);
    const existing = this.entries.get(key);
    if (existing) return existing;
    const entry: CatalogScopeEntry = {
      scopeType,
      scopeId,
      handlers: new Set(),
      subscribed: false,
      chain: Promise.resolve(),
      lastOut: null,
    };
    this.entries.set(key, entry);
    return entry;
  }

  private reconcile(entry: CatalogScopeEntry): Promise<void> {
    const step = entry.chain.then(async () => {
      const wantsSubscribe = entry.handlers.size > 0 && !entry.subscribed;
      const wantsUnsubscribe = entry.handlers.size === 0 && entry.subscribed;
      if (!wantsSubscribe && !wantsUnsubscribe) return;
      await this.waitReady();
      let subscribedNow = false;
      if (entry.handlers.size > 0 && !entry.subscribed) {
        await this.registrar.subscribe(this.deviceId(), entry.scopeType, entry.scopeId);
        entry.subscribed = true;
        subscribedNow = true;
      }
      if (entry.handlers.size === 0 && entry.subscribed && (subscribedNow || entry.lastOut === null)) {
        await this.registrar.unsubscribe(this.deviceId(), entry.scopeType, entry.scopeId);
        entry.subscribed = false;
      }
    });
    entry.chain = step.catch(() => undefined);
    return step;
  }
}
